import { AddIcon } from '@chakra-ui/icons';
import {
    Flex,
    FormControl,
    FormLabel,
    HStack,
    IconButton,
    useDisclosure,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsList, BsThreeDotsVertical } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useRegistrarFilter } from 'pages/resources/aors/hooks/useRegistrarFilter';
import useGetTRSRegistrations from './queries/useGetTRSRegistrations';
import { useRegistrarsMini } from './queries/useRegistrarsMini';

import { Button, Text } from '@/components/v4';
import { dropDownFormStyle } from 'components/forms/new/formStyles';
import { Error } from 'components/index';
import ActionsCellWrapper from 'components/tables/AGGrid/ActionsCellWrapper';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import Tooltip from 'components/v4/Tooltip';
import FeatureDisabledPage from 'pages/resources/feature-disabled-page';
import StatusCellRenderer from './cellRenderer/StatusCellRenderer';
import LogsModal from './LogsModal';
import AORStatuses from './utils/statuses';

import { tooltipChildrenStyles } from './constants';

const EnabledCellRenderer = ({ value }) => {
    return (
        <Flex alignItems="center" justify="center" height="100%">
            <Text fontSize="sm" fontWeight="medium">
                {value ? 'Enabled' : 'Disabled'}
            </Text>
        </Flex>
    );
};

// NOTE: TRS Registrations component (AORs)
export default function AORsIndex() {
    const gridRef = useRef();
    const navigate = useNavigate();
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { stateError, restError } = useSelector((state) => state.modal);
    const isNotEnabled = restError?.includes('not enabled') || false;
    const isError = stateError?.status === 400 || stateError?.status === 401;

    // Registrar filtering state
    const [selectedRegistrar, setSelectedRegistrar] = useState(null);

    // Logs modal state
    const [selectedAORForLogs, setSelectedAORForLogs] = useState(null);
    const [extensionName, setExtensionName] = useState('');
    const {
        isOpen: isOpenLogsModal,
        onOpen: onOpenLogsModal,
        onClose: onCloseLogsModal,
    } = useDisclosure();

    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const {
        data: registrars = [],
        isLoading: registrarsLoading,
        error: registrarsError,
    } = useRegistrarsMini(currentCompany);

    const { selectedRegistrarId, setRegistrarFilter } =
        useRegistrarFilter(registrars);

    const defaultLocationSearch = selectedRegistrarId
        ? `?registrar=${selectedRegistrarId}`
        : '';
    const selectedRegistrarValue =
        registrars?.find((r) => r.id === selectedRegistrarId) ||
        selectedRegistrar;
    const selectedRegistrarPlaceholder = registrarsLoading
        ? 'Loading registrars...'
        : 'Select registrar';
    const isSelectedDisabled = registrarsLoading || !registrars.length;
    const tooltipSelectLabel = isSelectedDisabled
        ? 'There is no Registrar available for this Company'
        : '';
    const handleRegistrarChange = (value) => {
        setRegistrarFilter(value?.id);
        setSelectedRegistrar(value);
    };

    const handleEditAOR = useCallback(
        (aorData) => {
            navigate(`/aors/edit/${aorData.id}${location.search}`);
        },
        [navigate],
    );

    const handleShowLogs = useCallback(
        (aor) => {
            setSelectedAORForLogs(aor.id);
            setExtensionName(aor.extension);
            onOpenLogsModal();
        },
        [onOpenLogsModal],
    );

    const handleCloseLogsModal = () => {
        onCloseLogsModal();
        setSelectedAORForLogs(null);
    };

    const handleAddAOR = () => {
        navigate(`/aors/add${location.search || defaultLocationSearch}`);
    };

    // TODO: wait for next Launch build
    // const handleCsvUpload = () => {
    //     navigate(
    //         `/aors/csv${selectedRegistrarId ? `?registrar=${selectedRegistrarId}` : ''}`,
    //     );
    // };

    useEffect(() => {
        if (registrars.length > 0 && !selectedRegistrar) {
            if (selectedRegistrarId) {
                const registrar = registrars.find(
                    (r) => r.id === selectedRegistrarId,
                );
                setSelectedRegistrar(registrar);
            } else {
                setSelectedRegistrar(registrars[0]);
            }
        }
    }, [registrars, selectedRegistrar, selectedRegistrarId]);

    const baseColDefs = useMemo(
        () => [
            {
                headerName: 'Extension',
                field: 'extension',
                cellRenderer: CopyCellRenderer,
                cellStyle: () => ({ width: '100%' }),
                flex: 1,
                minWidth: 120,
                sortable: true,
            },
            {
                headerName: 'Registrar',
                field: 'registrar.name',
                cellRenderer: CopyCellRenderer,
                flex: 1.2,
                minWidth: 150,
                sortable: true,
            },
            {
                headerName: 'Status',
                field: 'status',
                cellRenderer: (params) =>
                    StatusCellRenderer({
                        value: params.value,
                        // Handle backend typo: try both statusMessage and statusMesssage
                        statusMessage:
                            params.data.statusMessage ||
                            params.data.statusMesssage,
                    }),
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }),
                valueFormatter: (params) => {
                    // Priority logic: backend message first, then frontend mapping
                    const backendMessage =
                        params.data.statusMessage || params.data.statusMesssage;
                    if (backendMessage && backendMessage.trim() !== '') {
                        return backendMessage;
                    }

                    const statusConfig =
                        AORStatuses[params?.value] || AORStatuses.default;
                    return statusConfig.displayedName;
                },
                filter: 'agSetColumnFilter',
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        if (!isSelectedAll) {
                            const statusConfig =
                                AORStatuses[params?.value] ||
                                AORStatuses.default;
                            return (
                                <Text isTruncated>
                                    {statusConfig.displayedName}
                                </Text>
                            );
                        } else {
                            return '(Select All)';
                        }
                    },
                },
                flex: 1,
                minWidth: 120,
                sortable: true,
            },
            {
                headerName: 'Active',
                field: 'isEnabled',
                cellRenderer: (params) =>
                    EnabledCellRenderer({
                        value: params.value,
                        darkMode,
                    }),
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }),
                valueFormatter: (params) => {
                    return params.value ? 'Enabled' : 'Disabled';
                },
                filter: 'agSetColumnFilter',
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const value = params.value ? 'Enabled' : 'Disabled';
                        const content = isSelectedAll ? '(Select All)' : value;
                        return content;
                    },
                },
                flex: 1,
                minWidth: 100,
                sortable: true,
            },
            {
                headerName: 'Authentication',
                field: 'username',
                cellRenderer: (params) => {
                    const hasCustomAuth = !!(
                        params.data.username &&
                        params.data.password &&
                        params.data.username.trim() !== '' &&
                        params.data.password.trim() !== ''
                    );

                    return EnabledCellRenderer({
                        value: hasCustomAuth,
                        darkMode,
                    });
                },
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }),
                filter: 'agSetColumnFilter',
                filterParams: {
                    cellRenderer: (params) => {
                        console.log('filter column name: ', params);
                        const isSelectedAll = params?.value === '(Select All)';
                        if (isSelectedAll) {
                            return '(Select All)';
                        }

                        // params.value here is the boolean from valueGetter
                        const value = params.value ? 'Enabled' : 'Disabled';
                        return value;
                    },
                },
                flex: 1,
                minWidth: 120,
                sortable: true,
            },
        ],
        [darkMode],
    );

    // Actions column definition
    const actionsColDef = useMemo(
        () => ({
            maxWidth: 110,
            filter: false,
            suppressMovable: true,
            lockVisible: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            enableRowGroup: false,
            resizable: false,
            sortable: false,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
            }),
            lockPosition: 'right',
            cellRenderer: (params) => {
                return (
                    <ActionsCellWrapper>
                        <HStack
                            spacing={1}
                            justifyContent="center"
                            alignItems="center"
                            width="100%"
                            height="100%">
                            <Tooltip label="Edit AOR">
                                <IconButton
                                    icon={
                                        <BsThreeDotsVertical
                                            fill={darkMode ? 'white' : 'black'}
                                        />
                                    }
                                    aria-label="Edit"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        handleEditAOR(params.data);
                                    }}
                                />
                            </Tooltip>
                            <Tooltip label="Show logs">
                                <IconButton
                                    icon={
                                        <BsList
                                            fill={darkMode ? 'white' : 'black'}
                                        />
                                    }
                                    aria-label="Show logs"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        handleShowLogs(params.data);
                                    }}
                                />
                            </Tooltip>
                        </HStack>
                    </ActionsCellWrapper>
                );
            },
        }),
        [darkMode, handleEditAOR, handleShowLogs],
    );

    const colDefs = useMemo(() => {
        const columns = [...baseColDefs];
        columns.push(actionsColDef);
        return columns;
    }, [baseColDefs, actionsColDef]);

    const { refetch, isRefetching, isFetching, error } = useGetTRSRegistrations(
        setTableData,
        colDefs,
        selectedRegistrar?.id || null,
    );

    const isFetchingData = isRefetching || isFetching || registrarsLoading;

    const titleName = 'TRS Registrations';

    if (!currentCompany) {
        return (
            <Error
                error={{
                    name: 'No Partner / Company / Wholesaler Selected',
                    message:
                        'Please select a wholesaler and a partner / company.',
                }}
            />
        );
    }

    if (isError || isNotEnabled || error || registrarsError) {
        return <FeatureDisabledPage />;
    }

    return (
        <>
            <HStack justifyContent="space-between" marginBottom={5}>
                <Text sx={{ fontSize: '20', fontWeight: '500' }}>
                    TRS Registrations
                </Text>
            </HStack>

            <AGGridMain
                gridRef={gridRef}
                tableData={tableData}
                isLoading={isFetchingData}
                titleName={titleName}
                refetch={refetch}
                extraComponents={
                    <HStack pl={5} spacing={2}>
                        {/* TODO: wait for next Launch build */}
                        {/* <Button */}
                        {/*     minW="100px" */}
                        {/*     onClick={handleCsvUpload} */}
                        {/*     size="sm"> */}
                        {/*     CSV Update */}
                        {/* </Button> */}
                        <Button
                            minW="150px"
                            leftIcon={<AddIcon />}
                            onClick={handleAddAOR}
                            size="sm">
                            Add Registration
                        </Button>

                        {/* Registration Filter Dropdown */}
                        <FormControl className="!ml-[16px]">
                            <HStack
                                justifyContent="center"
                                w="500px"
                                sx={tooltipChildrenStyles}>
                                <FormLabel
                                    className="w-1/6 leading-4"
                                    fontSize="sm"
                                    mt={2}
                                    mr={0}>
                                    Filter by Registrar:
                                </FormLabel>
                                <Tooltip
                                    label={tooltipSelectLabel}
                                    shouldWrapChildren>
                                    <Select
                                        className="w-full"
                                        placeholder={
                                            selectedRegistrarPlaceholder
                                        }
                                        isLoading={registrarsLoading}
                                        value={selectedRegistrarValue}
                                        options={registrars}
                                        getOptionLabel={(option) => option.name}
                                        getOptionValue={(option) => option.id}
                                        onChange={(value) =>
                                            handleRegistrarChange(value)
                                        }
                                        isClearable={false}
                                        chakraStyles={dropDownFormStyle(
                                            darkMode,
                                        )}
                                        selectedOptionColor="brand"
                                        isDisabled={isSelectedDisabled}
                                    />
                                </Tooltip>
                            </HStack>
                        </FormControl>
                    </HStack>
                }
            />

            <LogsModal
                isOpen={isOpenLogsModal}
                onClose={handleCloseLogsModal}
                aorId={selectedAORForLogs}
                extensionName={extensionName}
            />
        </>
    );
}
